import React, { ReactElement, useContext, useState } from 'react'
// Commons imports.
// Sub imports.
// Contexts imports.
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    UserInfoContextProvider
} from '../Common/Contexts'
// Utilities imports.
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { clearModalHelpTopic, setModalHelpTopicID } from '../Common/Utils/HelpPortal'
import { DocTopic } from '../Common/Constants/Documentation'
import CMETabs from './CMETabs'
import { LanguageCME } from '../../../i18n'
import ViewOnlyContext, { ViewOnlyContextType } from '../Common/Contexts/ViewOnlyContext'
import { CommonFormContextProvider } from '../Common/Contexts/CommonFormContext'
import { Modal } from '../../ReactRouting/Components/Molecules/Modal/Modal'
import { $window } from '../../NonReact/common/AkixiWindow'

type Props = {
    filterCMEnvironmentID: string
    cmeID?: string
    refresh: () => void
    onModalExited?: () => void
}

/**
 * CME add/change modal wrapper used by the routed CME list.
 *
 * @param props - Modal props controlling the current CME context and callbacks.
 * @returns Modal tree for add, change, or view-only CME workflows.
 */
const CMEModal: React.FC<Props> = ({ cmeID, refresh, filterCMEnvironmentID, onModalExited }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(LanguageCME.NAME)

    const { isViewOnly }: ViewOnlyContextType = useContext(ViewOnlyContext)

    const [isModalOpen, setModalOpen] = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        setModalOpen(false)
    }

    return (
        <div>
            <Modal
                isOpen={isModalOpen}
                closeOnBackdropClick={true}
                isClosableWithXButton={true}
                inlineExitPromptSettings={{
                    message: language.get(LanguageCME.LanguageCME_PromptMessage),
                    confirmButtonText: language.get(LanguageCME.LanguageCME_PromptConfirm),
                    cancelButtonText: language.get(LanguageCME.LanguageCME_PromptCancel),
                    isRouterMode: true
                }}
                onOpen={(): void => setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)}
                onClose={closeModal}
                onExited={(): void => {
                    clearModalHelpTopic()
                    onModalExited?.()
                }}
                isViewOnly={isViewOnly}
            >
                {isModalOpen ? (
                    <LanguageContextProvider>
                        <PromptContextProvider>
                            <MessageContextProvider>
                                <UserInfoContextProvider>
                                    <CommonFormContextProvider>
                                        <div className='modal-title-report-config'>
                                            {cmeID
                                                ? language.get(LanguageCME.MainTitleModify)
                                                : language.get(LanguageCME.MainTitleAdd)}
                                        </div>
                                        <div className='cme-add-modify'>
                                            <CMETabs
                                                filterCMEnvironmentID={filterCMEnvironmentID}
                                                closeModal={closeModal}
                                                cmeID={cmeID}
                                                refresh={refresh}
                                            />
                                        </div>
                                    </CommonFormContextProvider>
                                </UserInfoContextProvider>
                            </MessageContextProvider>
                        </PromptContextProvider>
                    </LanguageContextProvider>
                ) : (
                    <></>
                )}
            </Modal>
        </div>
    )
}

export default CMEModal
